/**
 * 从服务端选项数组读取当前值对应的中文名称。
 *
 * @param {Array} options 服务端返回的 { value, label } 数组
 * @param {*} value 当前编码
 * @param {String} fallback 未匹配时的兜底文案
 * @returns {String}
 */
export function optionLabel(options, value, fallback) {
  const option = (options || []).find(item => item.value === value)
  return option ? option.label : fallback
}

/**
 * 返回版权审核状态对应的 Element 标签类型。
 *
 * @param {String} status 审核状态
 * @returns {String}
 */
export function verificationTagType(status) {
  return ({ verified: 'success', pending: 'warning', rejected: 'danger', unreviewed: 'info' })[status] || 'info'
}

/**
 * 返回版权风险等级对应的 Element 标签类型。
 *
 * @param {String} level 风险等级
 * @returns {String}
 */
export function riskTagType(level) {
  return ({ low: 'success', medium: 'warning', high: 'danger', critical: 'danger', unknown: 'info' })[level] || 'info'
}

/**
 * 返回展示策略预设对应的 Element 标签类型。
 *
 * @param {String} presetCode 展示策略编码
 * @returns {String}
 */
export function exposurePresetTagType(presetCode) {
  return ({ public: 'success', standard: 'warning', search_only: 'warning', hidden: 'danger', inherit: 'info' })[presetCode] || 'info'
}

/**
 * 判断管理列表行是否包含可试听地址。
 *
 * @param {Object} sound 音效列表行
 * @returns {Boolean}
 */
export function canPreviewSound(sound) {
  return Boolean(sound && sound.media_url)
}

/**
 * 隐去试听地址的文件名和参数，只显示工作人员判断来源所需的最短信息。
 *
 * @param {String} address 完整试听地址
 * @returns {String}
 */
export function maskPreviewAddress(address) {
  const value = String(address || '').trim().replace(/\\/g, '/')
  if (!value) return '地址不可用'

  const absoluteUrl = value.match(/^(https?:\/\/[^/]+)/i)
  if (absoluteUrl) return `${absoluteUrl[1]}/…`

  const segments = value.split('/').filter(Boolean)
  if (!segments.length) return '本地资源/…'
  return `${value.startsWith('/') ? '/' : ''}${segments.slice(0, 2).join('/')}/…`
}

/**
 * 读取服务端签发的后台试听地址及其来源类型。
 *
 * 管理页面禁止解释 sounds.path，也不能把 full_path 当作本地访问地址；所有
 * 本地资源都必须使用服务端返回的临时 media_url。
 *
 * @param {Object} sound 音效列表行
 * @returns {Object|null} 不可试听时返回 null
 */
export function buildSoundPreview(sound) {
  if (!canPreviewSound(sound)) return null
  const url = sound.media_url

  return {
    url,
    sourceType: sound.media_source === 'external' ? '外链' : '本地服务器',
    maskedAddress: maskPreviewAddress(url)
  }
}

/**
 * 返回后台音效来源的中文名称。
 *
 * 2026-09-28：来源必须以服务端 media_source 为准，不能再通过 URL 长相猜测。
 * 例如 file_asset 的试听地址也是 HTTP URL，但它仍然属于本地服务器资源。
 *
 * @param {String} source 服务端返回的 media_source
 * @returns {String}
 */
export function soundSourceLabel(source) {
  return ({
    asset: '本地服务器',
    legacy_local: '旧本地资源',
    external: '外链',
    unavailable: '无法识别'
  })[source] || '无法识别'
}

/**
 * 返回音效来源标签的 Element 类型。
 *
 * @param {String} source 服务端返回的 media_source
 * @returns {String}
 */
export function soundSourceTagType(source) {
  return ({ asset: 'success', legacy_local: 'warning', external: 'primary' })[source] || 'info'
}

/**
 * 返回资源检查状态的中文说明。
 *
 * 外链不会由服务端主动探测，避免恶意地址访问内网（SSRF）以及远端超时拖慢
 * 后台。因此 external_not_checked 表示“未检查”，不等于文件损坏。
 *
 * @param {String} status 服务端返回的 resource_status
 * @returns {String}
 */
export function resourceStatusLabel(status) {
  return ({
    available: '文件正常',
    file_missing: '文件缺失',
    asset_missing: '资源记录缺失',
    not_ready: '资源未就绪',
    invalid_kind: '资源类型错误',
    storage_error: '存储检查失败',
    legacy_unmanaged: '旧资源待迁移',
    external_not_checked: '外链未探测',
    unavailable: '资源不可用'
  })[status] || '资源不可用'
}

/**
 * 返回资源状态标签的 Element 类型。
 *
 * @param {String} status 服务端返回的 resource_status
 * @returns {String}
 */
export function resourceStatusTagType(status) {
  if (status === 'available') return 'success'
  if (status === 'external_not_checked') return 'info'
  if (status === 'legacy_unmanaged' || status === 'not_ready') return 'warning'
  return 'danger'
}
